import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import api from '../config/api';
import { queryKeys } from '../config/queryKeys';
import { useAuth } from './AuthContext';
import { getBuiltinRolePermissions } from '../config/rolePermissionDefaults';

const PermissionsContext = createContext(null);

/**
 * A successful /permissions/me payload is authoritative — an all-false map means
 * every grant was revoked, so builtins must never be layered back on top.
 */
function resolveFromPayload(data) {
  const raw = data?.permissions;
  // Coerce { name: true|false|1|0|"true" } into strict booleans
  const permissions = {};
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [name, v] of Object.entries(raw)) {
      permissions[name] = v === true || v === 1 || v === '1' || v === 'true';
    }
  }
  // Also accept granted name list when map missing/incomplete
  if (Array.isArray(data?.granted)) {
    for (const name of data.granted) {
      if (name) permissions[name] = true;
    }
  }
  const grantedNames = Object.entries(permissions)
    .filter(([, v]) => v)
    .map(([name]) => name);
  return {
    permissions,
    granted: grantedNames,
    usedFallback: false,
  };
}

export function PermissionsProvider({ children }) {
  const { user, sessionChecked } = useAuth();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery(
    queryKeys.permissionsMe(user?.id),
    async () => {
      const { data: payload } = await api.get('/permissions/me');
      return payload;
    },
    {
      enabled: Boolean(sessionChecked && user?.id),
      staleTime: 15_000,
      cacheTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: true,
    }
  );

  const resolved = useMemo(() => {
    if (!user?.id) {
      return { permissions: {}, granted: [], usedFallback: false };
    }
    if (data) return resolveFromPayload(data);
    if (isError) {
      const fallback = getBuiltinRolePermissions(user.role);
      return {
        permissions: fallback,
        granted: Object.keys(fallback),
        usedFallback: true,
      };
    }
    return { permissions: {}, granted: [], usedFallback: false };
  }, [data, isError, user]);

  const effectivePermissions = resolved.permissions;

  const hasPermission = useCallback(
    (name) => {
      if (!name) return true;
      return effectivePermissions[name] === true;
    },
    [effectivePermissions]
  );

  const hasAnyPermission = useCallback(
    (names) => {
      if (!names?.length) return true;
      return names.some((name) => effectivePermissions[name] === true);
    },
    [effectivePermissions]
  );

  const refreshPermissions = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['permissions', 'me'] });
    return refetch();
  }, [queryClient, refetch]);

  const loading =
    Boolean(user?.id) &&
    (isLoading || isFetching) &&
    !data &&
    !isError;

  const value = useMemo(
    () => ({
      permissions: effectivePermissions,
      granted: resolved.granted,
      loading,
      usedFallback: resolved.usedFallback,
      hasPermission,
      hasAnyPermission,
      refreshPermissions,
    }),
    [
      effectivePermissions,
      resolved.granted,
      resolved.usedFallback,
      loading,
      hasPermission,
      hasAnyPermission,
      refreshPermissions,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return ctx;
}
